import { useEffect, useState, useCallback, Fragment } from 'react';
import { useStatistics, useWindowDimensions } from "./Utilities"
import { isMobile, useMobileOrientation } from 'react-device-detect';
import { getData } from '../api/dataSlice';
import { useSelector } from "react-redux"
import { Grid } from '@mui/material'
import { setActivateDashboard } from './chatbot/ChatManager';
import dayjs from 'dayjs'
import Static from "./live/Static"
import StaticWithLive from './live/StaticWithLive';
import TimelapseContent from "./timelapse/TimelapseContent"
import ArchiveContent from "./archive/ArchiveContent"
import ArchiveControls from './archive/ArchiveControls'
import AccessContent from './access/AccessContent'
import DashboardContent from './dashboard/DashboardContent';

export default function Content({ component }) {

    const { CAMERAS, LAYOUT: { WRAP_LIMIT }, DEBUG } = window.conf
    const { isPortrait, isLandscape } = useMobileOrientation()
    const { initialFooterHeight, accessMonitoringCameraId } = useSelector(getData)
    const { contentHeight } = useWindowDimensions()
    
    const [isSideDrawerOpen, setIsSideDrawerOpen] = useState(false)
    const [paddingFooter, setPaddingFooter] = useState(0)
    const [mysiteView, setMysiteView] = useState(null) // null | 'dashboard' | 'timelapse' | 'access'
    const [dashboardState, setDashboardState] = useState(null)
    const isArchive = component === 3
    const isMySite = component === 4
    const isAccessMonitoring = component === 5 || (isMySite && mysiteView === 'access')
    const drawerBleeding = 22
    const initialSiderWidth = isMobile && isLandscape ? 280 : 332
    const camCount = isAccessMonitoring ? (isMobile ? 1 : 2) : CAMERAS.length
    const manyCameras = camCount > WRAP_LIMIT
    const hasControls = isArchive || isMySite || isAccessMonitoring
    const isStatisticsLoaded = useStatistics()
    const selectedAccessMonitoringCameraId = CAMERAS.find(elem => elem?.id === accessMonitoringCameraId) ?? CAMERAS[0]
    const camerasToRender = isAccessMonitoring
        ? isMobile
            ? [selectedAccessMonitoringCameraId]
            : [selectedAccessMonitoringCameraId, selectedAccessMonitoringCameraId]
        : CAMERAS;
    const contentWidth = hasControls
        ? isMobile
            ? isLandscape
                ? isSideDrawerOpen
                    ? `calc(100% - ${initialSiderWidth}px)`
                    : `calc(100% - ${drawerBleeding}px)`
                : '100%'
            : `calc(100% - ${initialSiderWidth}px)`
        : '100%'
    const contentHeightMobile = isMobile && isPortrait ? contentHeight - initialFooterHeight : contentHeight

    // update dashboardState.dateRange directly (used by DashboardFooter when DashboardControls is unmounted)
    const handleFooterDateRangeChange = useCallback((startISO, endISO) => {
        const start = dayjs(startISO)
        const end = dayjs(endISO)
        if (start.isValid() && end.isValid()) {
            setDashboardState(prev => prev ? { ...prev, dateRange: [start, end] } : prev)
        }
    }, [])

    // reset mysiteView on page change, register activation callback for chatbot on MYSITE
    useEffect(() => {
        if (isMySite) {
            setActivateDashboard(() => setMysiteView('dashboard'))
            return () => setActivateDashboard(null)
        }
        setMysiteView(null)
    }, [component]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        isStatisticsLoaded && DEBUG && console.log("[etracker] Successfully initialized")
    }, [isStatisticsLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

    function renderComponent(camera, renderIndex) {

        // MYSITE can override its per-camera view (e.g. timelapse, access monitoring).
        const effectiveComponent =
            isMySite && mysiteView === 'timelapse' ? 2 :
            isMySite && mysiteView === 'access' ? 5 :
            component

        switch (effectiveComponent) {

            // HOME
            case 0:
                return (
                    <Static
                        contentHeight={contentHeight}
                        camera={camera}
                    />
                )

            // LIVE
            case 1:
                if (camera.video) {
                    return (
                        <StaticWithLive
                            contentHeight={contentHeight}
                            camera={camera}
                        />
                    )
                } else {
                    return (
                        <Static
                            contentHeight={contentHeight}
                            camera={camera}
                        />
                    )
                }

            // TIMELAPSE
            case 2:
                return (
                    <TimelapseContent
                        contentHeight={contentHeightMobile}
                        camera={camera}
                        isSideDrawerOpen={isSideDrawerOpen}
                    />
                )

            // ARCHIVE & MYSITE
            case 3:
            case 4:
                return (
                    <ArchiveContent
                        contentHeight={contentHeightMobile}
                        camera={camera}
                        isSideDrawerOpen={isSideDrawerOpen}
                        paddingFooter={paddingFooter}
                    />
                )

            // ACCESS MONITORING
            case 5:
                return (
                    <AccessContent
                        camera={selectedAccessMonitoringCameraId}
                        direction={isMobile ? undefined : (renderIndex === 0 ? "entry" : "exit")}
                    />
                )

            default:
                break;
        }
    }

    return (
        <Fragment>

            {/* content */}

            {mysiteView === 'dashboard' ? (
                <DashboardContent
                    dashboardState={dashboardState}
                    contentHeight={contentHeightMobile}
                    contentWidth={contentWidth}
                />
            ) : (
                <Grid
                    container
                    columns={{
                        xs: !isMobile && isAccessMonitoring ? 2 : 1,
                        md: !isMobile && 2,
                        lg: isAccessMonitoring ? 2 : (manyCameras ? 3 : 2),
                        xl: isAccessMonitoring ? 2 : (manyCameras ? 4 : (camCount > 2 ? 3 : 2))
                    }}
                    sx={{
                        marginBottom: hasControls && isMobile && isPortrait ? `${initialFooterHeight}px` : 0,
                        width: contentWidth,
                        transition: '0.2s linear',
                        background: "black",
                        overflowX: "hidden",
                        paddingBottom: hasControls && isMobile && isPortrait ? `${paddingFooter}px` : 0,
                    }}
                >
                    {camerasToRender.map((camera, index) => (
                        <Grid
                            item
                            key={index}
                            xs={1}
                            md={camCount % 2 // amount of images is odd (e.g. 1, 3 or 5)
                                && index === camCount - 1 // image is last element of the array
                                && !manyCameras // do not extend last item for manyCameras grid
                                && !isAccessMonitoring // do not extend last item for access monitoring
                                ? 2
                                : 1
                            }
                            xl={!isAccessMonitoring && camCount > 2 ? 1 : undefined}
                            sx={{
                                backgroundColor: "black",
                                overflow: "hidden",
                                height: isAccessMonitoring
                                    ? isMobile
                                        ? contentHeightMobile
                                        : contentHeight
                                    : 'auto'
                            }}
                        >
                            {renderComponent(camera, index)}
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* controls */}

            {(isMySite || isArchive) && (
                <ArchiveControls
                    initialSiderWidth={initialSiderWidth}
                    onOpenSideDrawer={setIsSideDrawerOpen}
                    setPaddingFooter={setPaddingFooter}
                    drawerBleeding={drawerBleeding}
                    component={component}
                    onMysiteViewChange={setMysiteView}
                    mysiteView={mysiteView}
                    onDashboardStateChange={setDashboardState}
                    dashboardState={dashboardState}
                    onFooterDateRangeChange={handleFooterDateRangeChange}
                />
            )}

        </Fragment>
    )
}